import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import '../../global.css';
import NeuralCanvas from '../../components/NeuralCanvas';
import WordmarkLogo from '../../components/WordmarkLogo';
import AuthFooter from '../../components/AuthFooter';
import api from '../../store/axios';

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token');

  const [form, setForm] = useState({ password: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) setError('This reset link is invalid or has expired. Please request a new one.');
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await api.post('/auth/reset-password', { password: form.password }, { params: { token } });
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Reset failed. The link may have expired — request a new one.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-layout">
      <NeuralCanvas />

      <div className="signup-shell">
        <div className="signup-left">
          <WordmarkLogo size={22} />
          <div style={{ margin: 'auto 0', display: 'flex', flexDirection: 'column', gap: 32, maxWidth: 440 }}>
            <div className="dash-sub">NEW PASSWORD</div>
            <h1 className="signup-title">Almost<br /><em>there.</em></h1>
            <p className="signup-sub">Choose a strong password you haven't used before. Your account will be secured immediately.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 28, borderTop: '1px solid var(--line)' }}>
              {[
                ['8+ characters', 'Length is the single strongest factor'],
                ['Mix it up', 'Add numbers and symbols for extra strength'],
                ['Keep it unique', 'Don\'t reuse passwords from other sites'],
              ].map(([t, d], i) => (
                <div key={i} style={{ display: 'flex', gap: 16 }}>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--accent)', letterSpacing: '0.08em', flexShrink: 0, paddingTop: 2 }}>0{i + 1}</div>
                  <div>
                    <div style={{ fontSize: 14, color: 'var(--fg)', marginBottom: 4 }}>{t}</div>
                    <div style={{ fontSize: 13, color: 'var(--fg-mid)', lineHeight: 1.55 }}>{d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="signup-right">
          <form className="signup-form" onSubmit={handleSubmit}>
            <div>
              <div className="dash-sub" style={{ marginBottom: 10 }}>RESET PASSWORD</div>
              <h2 className="signup-title" style={{ fontSize: 30 }}>Set a new<br /><em>password.</em></h2>
            </div>

            {error && <div className="alert alert-error">{error}</div>}

            {done ? (
              <div className="alert alert-success">
                Password updated. Taking you to sign in…
              </div>
            ) : (
              <>
                <div className="field">
                  <label>New password</label>
                  <input
                    type="password"
                    required
                    placeholder="8+ characters"
                    disabled={!token}
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                  />
                </div>
                <div className="field">
                  <label>Confirm password</label>
                  <input
                    type="password"
                    required
                    placeholder="Same as above"
                    disabled={!token}
                    value={form.confirm}
                    onChange={e => setForm({ ...form, confirm: e.target.value })}
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary btn-lg"
                  style={{ justifyContent: 'center' }}
                  disabled={loading || !token}
                >
                  {loading ? <><span className="btn-loader" /> Saving…</> : 'Set new password'}
                </button>
              </>
            )}

            <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--fg-dim)' }}>
              <Link to="/forgot-password" style={{ color: 'var(--fg-mid)' }}>Request a new reset link</Link>
            </div>
          </form>
        </div>
      </div>

      <AuthFooter />
    </div>
  );
}
